/**
 * Personalakte-Tab „Abwesenheiten": Urlaubskonto-Karte des aktuellen Jahres
 * (+ „Urlaub beantragen", Phase 3) sowie Liste des Mitarbeiters mit
 * Grund-Badges + Anlage-Dialog (Einzel/Bereich → bulkCreate mit
 * Betriebstage-Toast).
 */

import { useMemo, useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { Palmtree, Plus, Trash2 } from "lucide-react"
import { toast } from "sonner"
import { AbsenceFormSheet } from "@/components/absences/AbsenceFormSheet"
import { LeaveBalanceCard } from "@/components/vacation/LeaveBalanceCard"
import { VacationRequestDialog } from "@/components/vacation/VacationRequestDialog"
import { LoadingSpinner } from "@/components/shared"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import { DataGrid, type ColumnDef } from "@/components/ui/data-grid"
import { ABSENCE_REASONS, ABSENCE_STATUS_LABEL } from "@/lib/absenceColors"
import { fmtDate } from "@/lib/format"
import { api, apiError } from "@/services/api"
import type { Absence } from "@/types"

export function EmployeeAbsencesTab({
  employeeId,
  canWrite,
}: {
  employeeId: string
  canWrite: boolean
}) {
  const queryClient = useQueryClient()
  const [dialogOpen, setDialogOpen] = useState(false)
  const [vacationOpen, setVacationOpen] = useState(false)
  const currentYear = new Date().getFullYear()

  const query = useQuery({
    queryKey: ["absences", "employee", employeeId],
    queryFn: () => api.absences.list({ employee: Number(employeeId), size: 100 }),
  })

  // Urlaubskonto des aktuellen Jahres (Phase 3, AP 3.3)
  const balance = useQuery({
    queryKey: ["leave", "account", employeeId, currentYear],
    queryFn: () => api.leave.account.get(employeeId, currentYear),
  })

  const del = useMutation({
    mutationFn: (id: string) => api.absences.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["absences"] })
      queryClient.invalidateQueries({ queryKey: ["employee", employeeId] })
      queryClient.invalidateQueries({ queryKey: ["employees"] })
      queryClient.invalidateQueries({ queryKey: ["leave"] })
      toast.success("Abwesenheit gelöscht")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  const rows = query.data?.data ?? []

  // Grid-Spalten: header als String, damit die Spalten-Auswahl ihn anzeigen kann
  const columns = useMemo<ColumnDef<Absence>[]>(() => {
    const defs: ColumnDef<Absence>[] = [
      {
        id: "period",
        header: "Zeitraum",
        // ISO-Startdatum sortiert chronologisch korrekt
        accessorFn: (a) => a.startDate,
        cell: ({ row }) => (
          <>
            {fmtDate(row.original.startDate)} – {fmtDate(row.original.endDate)}
          </>
        ),
        meta: { cellClassName: "font-medium tabular-nums" },
      },
      {
        id: "reason",
        header: "Grund",
        accessorFn: (a) => ABSENCE_REASONS[a.reason].label,
        cell: ({ row }) => (
          <Badge variant="outline" className={ABSENCE_REASONS[row.original.reason].badge}>
            {ABSENCE_REASONS[row.original.reason].label}
          </Badge>
        ),
      },
      {
        id: "status",
        header: "Status",
        accessorFn: (a) => ABSENCE_STATUS_LABEL[a.status],
        meta: { cellClassName: "text-muted-foreground" },
      },
    ]
    if (canWrite) {
      defs.push({
        id: "aktionen",
        header: "Aktionen",
        enableSorting: false,
        enableHiding: false,
        meta: { headClassName: "w-20 text-right", cellClassName: "text-right" },
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="size-7 text-destructive hover:text-destructive"
            aria-label="Löschen"
            disabled={del.isPending}
            onClick={() => {
              if (window.confirm("Abwesenheit wirklich löschen?")) del.mutate(row.original.id)
            }}
          >
            <Trash2 className="!size-3.5" />
          </Button>
        ),
      })
    }
    return defs
  }, [canWrite, del])

  return (
    <div className="space-y-3">
      {/* Urlaubskonto-Karte + Antrag (Phase 3) */}
      {balance.data && (
        <LeaveBalanceCard
          balance={balance.data}
          actions={
            <Button variant="outline" size="sm" onClick={() => setVacationOpen(true)}>
              <Palmtree /> Urlaub beantragen
            </Button>
          }
        />
      )}
      {canWrite && (
        <div className="flex justify-end">
          <Button onClick={() => setDialogOpen(true)}>
            <Plus /> Abwesenheit anlegen
          </Button>
        </div>
      )}
      <Card>
        <CardContent className="py-2">
          {query.isLoading ? (
            <LoadingSpinner fullPage />
          ) : (
            <DataGrid
              columns={columns}
              data={rows}
              storageKey="employee-absences"
              emptyMessage="Keine Abwesenheiten erfasst."
            />
          )}
        </CardContent>
      </Card>

      <AbsenceFormSheet
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        fixedEmployeeId={employeeId}
      />
      <VacationRequestDialog
        open={vacationOpen}
        onOpenChange={setVacationOpen}
        fixedEmployeeId={employeeId}
      />
    </div>
  )
}
